import dayjs from "dayjs"

const getTotalInsuredAmount = policy => {
	const totalSumInsured = policy?.facts?.find(
		item => item.type === "totalSumInsured"
	)?.value.numberValue
	return totalSumInsured ? totalSumInsured : 0
}

/** Check if the policy is a result of an endorsement **/
const isEndorsement = transaction => {
	const type = transaction?.object?.__typename
	return type === "endorsement" ? true : false
}

const getBinderNumber = policy => {
	return policy?.associatedContracts?.[0]?.contract?.facts?.find(
		fact => fact.type === "binderNumber"
	)?.value?.stringValue
}

const getBinderFact = (type, policy) => {
	return policy?.associatedContracts?.[0]?.contract?.facts?.find(
		item => item.type === type
	)?.value?.stringValue
}

const getPolicyFact = (type, policy) => {
	return policy?.facts?.find(item => item.type === type)?.value?.stringValue
}

/**
 *
 * @param {*} policy
 * returns contractHolder Object or contractInsured Array depending on product type of policy
 */
const getAssured = policy => {
	let type = policy?.product?.productId?.type
	if (
		type === "jewellers_block" ||
		type === "employees_compensation" ||
		type === "commercial_block"
	) {
		return policy?.contractInsured?.[0]
	} else {
		return policy?.contractHolder
	}
}

const getCountry = userObj => {
	return userObj?.addresses?.[0]?.fields2?.find(item => item.type === "country")
		?.value
}

const getAddress = userObj => {
	return userObj?.addresses?.[0]?.fields2
		?.filter(add => {
			if (
				add?.type === "addressLine1" ||
				add?.type === "addressLine2" ||
				add?.type === "addressLine3"
			)
				return true
		})
		.map(add => add?.value)
		?.join(",")
}

const getCity = userObj => {
	return userObj?.addresses?.[0]?.fields2?.find(item => item.type === "city")
		?.value
}

const getPostalCode = userObj => {
	return userObj?.addresses?.[0]?.fields2?.find(item => item.type === "zipCode")
		?.value
}

const getTransactionsFact = (type, res, transaction) => {
	return transaction?.facts?.find(item => item.type === type)?.value?.[res]
}

const getInsuredAmountCurrency = policy => {
	return policy?.product?.pricing?.currencyCode
}

const getInsuranceType = policy => {
	let type = policy?.product?.productId?.type
	return type === "jewellers_block"
		? "JB"
		: type === "employees_compensation"
		? "EC"
		: type === "jewellers_tour"
		? "TO"
		: type === "sending"
		? "SI"
		: type === "commercial_block"
		? "CB"
		: type === "public_liability"
		? "PL"
		: type === "personal_jewellery"
		? "PJ"
		: type === "jewellers_exhibition"
		? "EX"
		: ""
}

const getClaimFact = (type, res, claim) => {
	const value = claim?.facts?.find(item => item.type === type)?.value?.[res]
	return res === "numberValue" && !value ? 0 : value
}

const getLloydsRiskCode = policy => {
	let type = policy?.product?.productId?.type
	return type === "jewellers_block"
		? "JB"
		: type === "employees_compensation"
		? "NR"
		: type === "jewellers_tour"
		? "JB"
		: type === "sending"
		? "JB"
		: type === "commercial_block"
		? "JB"
		: type === "public_liability"
		? "NR"
		: type === "personal_jewellery"
		? "JB"
		: type === "jewellers_exhibition"
		? "JB"
		: ""
}

/**
 *  if transaction is linked to a policy, we only care about the original state before the first endorsement
 *  if its a transaction linked to an endorsement we get the state of that policy before any other endorsement
 *  type is defined based on the typename of object inside transaction
 *  1. if type is policy , first we check if policy has an endorsement, if not then we get the policy object directly,
 *  2. otherwise if the policy has an endorsement, we get the first endorsement item and get original state of policy from `beforeEndorsement`
 *  3. if type is endorsement, we get the `afterEndorsement` object from endorsement
 */
const getPolicyFromTransactionObj = transaction => {
	const type = transaction?.object?.__typename
	return type === "policy" && transaction?.object?.endorsements.length === 0
		? transaction?.object
		: type === "policy" && transaction?.object?.endorsements.length > 0
		? transaction?.object?.endorsements?.[0]?.beforeEndorsement
		: type === "endorsement"
		? transaction?.object?.afterEndorsement
		: {}
}

const formatDate = date => {
	return dayjs(date).format("YYYY/MM/DD")
}

const getTransactionTypeHKReport = transaction => {
	return transaction?.object?.__typename === "policy" &&
		transaction?.object?.proposal?.renewalHistory?.renewedFrom === null
		? "OP"
		: transaction?.object?.__typename === "endorsement" &&
		  transaction?.status === "APPROVED"
		? "AP"
		: transaction?.object?.__typename === "endorsement" &&
		  transaction?.status === "REFUNDED"
		? "RP"
		: ""
}

const getTransactionTypeLondonReport = transaction => {
	return transaction?.object?.__typename === "policy" &&
		transaction?.object?.proposal?.renewalHistory?.renewedFrom === null
		? "New"
		: transaction?.object?.__typename === "policy" &&
		  transaction?.object?.proposal?.renewalHistory?.renewalFrom !== null
		? "Renewal"
		: transaction?.object?.__typename === "endorsement" &&
		  transaction?.status === "APPROVED"
		? "AP"
		: transaction?.object?.__typename === "endorsement" &&
		  transaction?.status === "REFUNDED"
		? "RP"
		: transaction?.object?.__typename === "policy" &&
		  transaction?.status === "CANCELLED"
		? "Cancellation"
		: ""
}

const getCustomerType = policy => {
	let type = policy?.product?.productId?.type
	return type === "personal_jewellery" ? "CONSUMER" : "MICRO"
}

const getCommissionRatio = (type, policy) => {
	const ratio = policy?.commissionSplitRules?.find(
		item => item?.remark === type
	)?.ratio
	return ratio ? ratio : 0
}
const getCommissionEntityName = (type, policy) => {
	const name = policy?.commissionSplitRules?.find(item => item?.remark === type)
		?.entity?.name
	return name
}

const getCommissionAmount = (type, amount, policy) => {
	const rule = policy?.commissionSplitRules?.find(item => item?.remark === type)
	// NOTE: If this is a flat fee, we can get the commissionSplit amount
	if (rule?.type === "flat") {
		return rule?.amount
	}
	// NOTE: If it's a ratio we do not get commission amount directly from commissionSplit because payments can be made in installments
	// and amount in commissionSplit would not reflect the true commissionSplit per transaction
	const ratio = rule?.ratio
	const output = amount && ratio ? amount * ratio : 0
	return parseFloat(output?.toFixed(2))
}

export {
	getTotalInsuredAmount,
	isEndorsement,
	getBinderNumber,
	getBinderFact,
	getPolicyFact,
	getAssured,
	getCountry,
	getAddress,
	getCity,
	getPostalCode,
	getTransactionsFact,
	getInsuredAmountCurrency,
	getInsuranceType,
	getClaimFact,
	getLloydsRiskCode,
	getPolicyFromTransactionObj,
	formatDate,
	getTransactionTypeHKReport,
	getTransactionTypeLondonReport,
	getCustomerType,
	getCommissionRatio,
	getCommissionAmount,
	getCommissionEntityName
}
